import { useState } from 'react';

// material-ui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

// third party
import { useSnackbar } from 'notistack';

// project imports
import { useDispatch } from 'store';
import { acceptConsent } from 'store/slices/auth';
import { useNavTheme } from 'contexts/NavThemeContext';

// assets
import { IconShieldCheck } from '@tabler/icons-react';

interface ConsentModalProps {
  open: boolean;
  onComplete: () => void;
}

export default function ConsentModal({ open, onComplete }: ConsentModalProps) {
  const [loading, setLoading] = useState(false);
  const [acceptTos, setAcceptTos] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { mode, tokens } = useNavTheme();
  const isLight = mode === 'light';

  // Monochrome button colors
  const primaryButtonBg = isLight ? '#1e293b' : '#fafafa';
  const primaryButtonText = isLight ? '#fafafa' : '#0a0a0f';

  const handleAcceptConsent = async () => {
    if (!acceptTos || !acceptPrivacy) {
      enqueueSnackbar('You must accept both the Terms of Service and Privacy Policy', {
        variant: 'warning',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });
      return;
    }

    try {
      setLoading(true);

      await dispatch(acceptConsent({
        acceptTos: true,
        acceptPrivacyPolicy: true,
        marketingConsent,
      })).unwrap();

      enqueueSnackbar('Thank you for accepting our terms!', {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });

      onComplete();
    } catch (error: any) {
      console.error('Failed to record consent:', error);
      enqueueSnackbar('Something went wrong. Please try again.', {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = acceptTos && acceptPrivacy && !loading;

  const linkStyle = {
    color: isLight ? '#4f46e5' : '#818cf8',
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        paper: {
          sx: {
            background: isLight ? '#ffffff' : 'rgba(15, 16, 24, 0.98)',
            border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            borderRadius: '16px',
            boxShadow: isLight
              ? '0 25px 80px rgba(15, 23, 42, 0.15)'
              : '0 25px 80px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
          }
        },
        backdrop: {
          sx: {
            backgroundColor: isLight ? 'rgba(15, 23, 42, 0.4)' : 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 0, pt: 4, px: 4 }}>
        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
            }}
          >
            <IconShieldCheck size={28} style={{ color: tokens.textMuted }} />
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                fontFamily: '"Satoshi", "Inter", sans-serif',
                color: tokens.textPrimary,
                letterSpacing: '-0.01em',
                mb: 0.5,
              }}
            >
              Welcome to Synlets
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontFamily: '"Inter", sans-serif',
                color: tokens.textMuted,
                lineHeight: 1.6,
              }}
            >
              Please review and accept our policies to continue.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 1, pb: 0 }}>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptTos}
                onChange={(e) => setAcceptTos(e.target.checked)}
                disabled={loading}
                sx={{
                  color: tokens.textMuted,
                  '&.Mui-checked': {
                    color: isLight ? '#1e293b' : '#fafafa',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.875rem', color: tokens.textSecondary }}>
                I agree to the{' '}
                <Box
                  component="a"
                  href="https://synlets.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={linkStyle}
                >
                  Terms of Service
                </Box>
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptPrivacy}
                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                disabled={loading}
                sx={{
                  color: tokens.textMuted,
                  '&.Mui-checked': {
                    color: isLight ? '#1e293b' : '#fafafa',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.875rem', color: tokens.textSecondary }}>
                I agree to the{' '}
                <Box
                  component="a"
                  href="https://synlets.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={linkStyle}
                >
                  Privacy Policy
                </Box>
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                disabled={loading}
                sx={{
                  color: tokens.textMuted,
                  '&.Mui-checked': {
                    color: isLight ? '#1e293b' : '#fafafa',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.875rem', color: tokens.textMuted }}>
                I'd like to receive product updates and news (optional)
              </Typography>
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 3 }}>
        <Box
          component="button"
          type="button"
          disabled={!canSubmit}
          onClick={handleAcceptConsent}
          sx={{
            width: '100%',
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderRadius: '10px',
            background: canSubmit ? primaryButtonBg : (isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'),
            border: 'none',
            color: canSubmit ? primaryButtonText : tokens.textMuted,
            fontSize: '0.95rem',
            fontWeight: 600,
            fontFamily: '"Satoshi", sans-serif',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            opacity: canSubmit ? 1 : 0.6,
            transition: 'all 0.15s ease',
            '&:hover:not(:disabled)': {
              background: isLight ? '#0f172a' : '#ffffff',
              transform: canSubmit ? 'translateY(-1px)' : 'none',
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={18} sx={{ color: primaryButtonText }} />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <IconShieldCheck size={18} />
              <span>Continue to Platform</span>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
